import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { apiFetch } from '../api';

const API = '/api/leads';

const MODES = [
  { key: 'add',     label: 'Nur neue Leads hinzufügen',
    hint: 'Bekannte HA-IDs werden übersprungen — Standard.' },
  { key: 'update',  label: 'Neue hinzufügen + Stammdaten bestehender aktualisieren',
    hint: 'Adresse, Rabattcode, Montagetermin usw. werden aus der Datei übernommen. Status, Notizen, Verlauf und Zuweisung bleiben. Koordinaten werden nur ergänzt, nie ersetzt.' },
  { key: 'replace', label: 'Alles ersetzen',
    hint: 'Löscht ALLE Leads samt Status, Verlauf, Statistik und Lead-Terminen und importiert die Datei neu.' },
];

const num = n => (n ?? 0).toLocaleString('de-DE');

// "2026-09-08 19:22:57" (UTC aus der DB) → "08.09.2026"
const importDatum = s => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T') + (s.endsWith('Z') ? '' : 'Z'));
  return isNaN(d) ? '' : d.toLocaleDateString('de-DE');
};

export default function ImportModal({ onClose, onImported, onDataChanged, authHeader }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('add');
  const fileRef = useRef();

  const [pendingFile, setPendingFile] = useState(null);

  // Löschen nach Stadt — eigener Bereich, standardmäßig zugeklappt, damit die
  // destruktive Aktion nicht neben dem Datei-Feld liegt
  const [showDelete, setShowDelete] = useState(false);
  const [cities, setCities] = useState(null);
  const [citiesError, setCitiesError] = useState('');
  const [selected, setSelected] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  const loadCities = useCallback(async () => {
    setCitiesError('');
    try {
      const res = await apiFetch(`${API}/cities`, { headers: authHeader });
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      setCities(await res.json());
    } catch (err) {
      console.error('Städte laden fehlgeschlagen:', err);
      setCitiesError('Städte konnten nicht geladen werden.');
    }
  }, [authHeader]);

  useEffect(() => { if (showDelete && cities === null) loadCities(); }, [showDelete, cities, loadCities]);

  // Löschen passiert serverseitig in derselben Transaktion wie der Import —
  // schlägt der Import fehl, bleiben die bestehenden Leads erhalten
  const doImport = async file => {
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', mode);
      const res = await apiFetch(`${API}/import/xlsx`, { method: 'POST', headers: authHeader, body: formData });
      if (!res.ok) throw new Error(`Import fehlgeschlagen (${res.status})`);
      setResult(await res.json());
      setCities(null); // Städte-Liste ist nach einem Import veraltet
      setSelected([]);
    } catch (err) {
      console.error('Import-Fehler:', err);
      setError('Import fehlgeschlagen — bitte Datei und Verbindung prüfen, dann erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // Während eines laufenden Imports keine zweite Datei annehmen (paralleler POST)
  const handleFile = file => {
    if (!file || loading) return;
    if (mode === 'replace') { setPendingFile(file); return; }
    doImport(file);
  };

  const toggleCity = ort =>
    setSelected(s => (s.includes(ort) ? s.filter(o => o !== ort) : [...s, ort]));

  // Summe dessen, was an der Auswahl hängt — Grundlage für die Warnung im Dialog
  const chosen = (cities || []).filter(c => selected.includes(c.ort));
  const totals = chosen.reduce((acc, c) => ({
    leads:      acc.leads      + c.leads,
    bearbeitet: acc.bearbeitet + c.bearbeitet,
    geclosed:   acc.geclosed   + c.geclosed,
    zugewiesen: acc.zugewiesen + c.zugewiesen,
    termine:    acc.termine    + c.termine,
  }), { leads: 0, bearbeitet: 0, geclosed: 0, zugewiesen: 0, termine: 0 });

  const doDelete = async () => {
    setDeleting(true);
    setCitiesError('');
    setDeleteResult(null);
    try {
      const res = await apiFetch(`${API}/by-city`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ orte: selected }),
      });
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      const data = await res.json();
      setDeleteResult(data);
      setSelected([]);
      setCities(null);
      await loadCities();
      onDataChanged?.(); // Karte/Liste im Hintergrund neu laden — Modal bleibt offen
    } catch (err) {
      console.error('Löschen fehlgeschlagen:', err);
      setCitiesError('Löschen fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      setDeleting(false);
    }
  };

  const deleteWarning = [
    `${num(totals.leads)} Leads werden gelöscht.`,
    totals.bearbeitet > 0 && `Davon ${num(totals.bearbeitet)} bereits bearbeitet — Status, Notizen und der komplette Verlauf gehen mit verloren.`,
    totals.geclosed   > 0 && `${num(totals.geclosed)} Abschlüsse verschwinden damit auch aus der Statistik.`,
    totals.zugewiesen > 0 && `${num(totals.zugewiesen)} Leads sind aktuell Vertrieblern zugewiesen.`,
    totals.termine    > 0 && `${num(totals.termine)} Termine an diesen Leads werden mitgelöscht.`,
    'Das lässt sich nicht rückgängig machen.',
  ].filter(Boolean).join('\n\n');

  return (
    <Modal onClose={onClose} label="Excel importieren">
        <h2>Excel importieren</h2>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
          Panda-Promotion Format (HA-ID, Gauß-Krüger Koordinaten)
        </p>
        <div role="radiogroup" aria-label="Import-Modus" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {MODES.map(m => (
            <label key={m.key} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${mode === m.key ? (m.key === 'replace' ? '#fca5a5' : '#a78bfa') : '#e5e7eb'}`,
              background: mode === m.key ? (m.key === 'replace' ? '#fef2f2' : '#f5f3ff') : 'white',
            }}>
              <input type="radio" name="import-mode" value={m.key} checked={mode === m.key} onChange={() => setMode(m.key)} style={{ marginTop: 3 }} />
              <span>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: m.key === 'replace' ? '#b91c1c' : '#1e1b4b' }}>{m.label}</span>
                <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2 }}>{m.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <div
          className="import-zone"
          role="button"
          tabIndex={0}
          aria-disabled={loading}
          aria-label="Excel-Datei auswählen oder ablegen"
          onClick={() => { if (!loading) fileRef.current.click(); }}
          onKeyDown={e => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            if (!loading) fileRef.current.click();
          }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        >
          <p><strong>Datei ablegen</strong> oder klicken</p>
          <p style={{ marginTop: 6, fontSize: 12 }}>.xlsx / .xls</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
        </div>
        {loading && <p style={{ textAlign: 'center', color: '#6b7280' }}>Importiere...</p>}
        {error && (
          <div className="login-error" role="alert" style={{ marginBottom: 8 }}>{error}</div>
        )}
        {result && (
          <div className="import-result">
            {result.imported} Leads importiert.
            {result.updated > 0 && <span> · {result.updated} aktualisiert</span>}
            {result.assigned > 0 && <span> · {result.assigned} per Gebiet zugewiesen</span>}
            {result.geeInvalid > 0 && <span style={{ color: '#92400e' }}> · {result.geeInvalid}× GEE weder Ja noch Nein (nicht übernommen)</span>}
            {result.skipped > 0 && <span style={{ color: '#92400e' }}> · {result.skipped} bereits vorhanden (übersprungen)</span>}
            {result.errors?.length > 0 && <span style={{ color: '#dc2626' }}> · {result.errors.length} Fehler</span>}
          </div>
        )}

        {/* ---- Leads nach Stadt löschen ---- */}
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 18, paddingTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowDelete(v => !v)}
            aria-expanded={showDelete}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, color: '#b91c1c',
            }}
          >
            {showDelete ? '▾' : '▸'} Leads nach Stadt löschen
          </button>

          {showDelete && (
            <div style={{ marginTop: 10 }}>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                Entfernt einen kompletten Ort — z.B. um einen versehentlich doppelt
                importierten Datensatz loszuwerden, ohne den restlichen Bestand anzufassen.
              </p>

              {citiesError && <div className="login-error" role="alert" style={{ marginBottom: 8 }}>{citiesError}</div>}
              {cities === null && !citiesError && <p style={{ fontSize: 13, color: '#6b7280' }}>Lade Städte...</p>}
              {cities?.length === 0 && <p style={{ fontSize: 13, color: '#6b7280' }}>Keine Leads vorhanden.</p>}

              {cities?.length > 0 && (
                <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  {cities.map(c => (
                    <label key={c.ort} style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      background: selected.includes(c.ort) ? '#fef2f2' : 'white',
                    }}>
                      <input
                        type="checkbox"
                        checked={selected.includes(c.ort)}
                        onChange={() => toggleCity(c.ort)}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1e1b4b' }}>
                          {c.ort} <span style={{ fontWeight: 400, color: '#6b7280' }}>· {num(c.leads)} Leads</span>
                        </span>
                        <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          {num(c.bearbeitet)} bearbeitet · {num(c.geclosed)} Abschlüsse · {num(c.zugewiesen)} zugewiesen
                          {c.termine > 0 && ` · ${num(c.termine)} Termine`}
                          {importDatum(c.letzter_import) && ` · Import ${importDatum(c.letzter_import)}`}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {deleteResult && (
                <div className="import-result" style={{ marginTop: 10 }}>
                  {num(deleteResult.deleted)} Leads gelöscht
                  {deleteResult.orte?.length > 0 && ` (${deleteResult.orte.join(', ')})`}.
                </div>
              )}

              {selected.length > 0 && (
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{ marginTop: 10 }}
                  disabled={deleting}
                  onClick={() => setPendingDelete(true)}
                >
                  {deleting
                    ? 'Lösche...'
                    : `${num(totals.leads)} Leads aus ${selected.length} ${selected.length === 1 ? 'Ort' : 'Orten'} löschen`}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button className="btn btn-secondary" onClick={onClose}>Schließen</button>
          {result && <button className="btn btn-primary" onClick={onImported}>Karte anzeigen</button>}
        </div>

        {pendingFile && (
          <ConfirmDialog
            title="Wirklich alles ersetzen?"
            message={'Alle Leads werden gelöscht — mit ihnen Status, Notizen, der komplette Verlauf (Grundlage der Statistik) und alle Lead-Termine. Das lässt sich nicht rückgängig machen.\n\nSchlägt der Import fehl, bleiben die alten Daten erhalten.\n\nWenn nur Stammdaten aktualisiert werden sollen, ist „Neue hinzufügen + Stammdaten aktualisieren“ die sichere Wahl.'}
            confirmLabel="Alles ersetzen"
            onConfirm={() => { const f = pendingFile; setPendingFile(null); doImport(f); }}
            onCancel={() => setPendingFile(null)}
          />
        )}
        {pendingDelete && (
          <ConfirmDialog
            title={`${selected.join(', ')} löschen?`}
            message={deleteWarning}
            confirmLabel={`${num(totals.leads)} Leads löschen`}
            onConfirm={() => { setPendingDelete(false); doDelete(); }}
            onCancel={() => setPendingDelete(false)}
          />
        )}
    </Modal>
  );
}
